import { DateTime } from 'luxon';
import { FormGroup } from '@angular/forms';
import { Job } from './models/tool.models';

export type AtLeast<T, K extends keyof T> = Partial<T> & Pick<T, K>;

/**
 * Helper function to format datetime to a locale string
 * @param datetime which will be transformed
 */
export function formatDateTime(datetime: string): string {
  const date = DateTime.fromISO(datetime);
  if (!date.isValid) return datetime;
  if (date.hasSame(DateTime.local(), 'day')) return date.toLocaleString(DateTime.TIME_SIMPLE);
  return date.toLocaleString(DateTime.DATETIME_SHORT);
}

/**
 * Encode Input to send JSON and Lists; Files should not be encoded!
 *
 * @param value value which will be formatted
 */
function formatValue(value: unknown) {
  if (value instanceof File) {
    return value;
  }
  return JSON.stringify(value);
}

/**
 * Transform formGroup Data into FormData
 * Can handle deep forms (e.g. formgroup in formgroup)
 * can handle files
 * will format value to JSON String
 *
 * @param formGroup form group to be transformed
 */
export function toFormData(formGroup: FormGroup) {
  const formData = new FormData();
  for (const key in formGroup.controls) {
    if (formGroup.controls[key].value instanceof FormGroup) {
      const innerGroup = formGroup.controls[key].value;
      const innerGroupData = toFormData(innerGroup);
      innerGroupData.forEach(value => {
        formData.append(key, value);
      });
    } else {
      const formValue = formGroup.controls[key].value;
      if (formValue !== null && formValue !== undefined && formValue !== '') {
        const value = formatValue(formValue);
        formData.append(key, value);
      }
    }
  }
  return formData;
}

/**
 * Method to check order of job modified dates.
 * Can be used to sort jobs.
 *
 * @param jobA first job to compare
 * @param jobB second job to compare
 */
export function sortJobsByDate(jobA: Job, jobB: Job) {
  const jobATime = DateTime.fromISO(jobA.modified);
  const jobBTime = DateTime.fromISO(jobB.modified);
  if (jobATime > jobBTime) {
    return -1;
  } else if (jobATime < jobBTime) {
    return 1;
  }
  return 0;
}

/**
 * Method to check if a value is set.
 * Checks for null, undefined and empty string.
 *
 * @param val value to check
 */
export function hasValue<T>(val: T | null | undefined): boolean {
  return val !== null && val !== undefined && val !== '';
}

/**
 * Method to transform a value to an array if
 * not yet an array.
 */
export function toArray<T>(val: T | T[] | null | undefined): T[] {
  if (val === null || val === undefined || val === '') return [];
  return Array.isArray(val) ? val : [val];
}

export function toRecord<T>(value: Record<string, T> | Record<string, T>[]): Record<string, T> {
  return Array.isArray(value) ? Object.assign({}, ...value) : value;
}

export function fileNameFromValue(value: unknown): string | undefined {
  if (typeof File !== 'undefined' && value instanceof File) return value.name;
  if (typeof value === 'string') return value.split(/[\\/]/).pop() || undefined;
  if (!value || typeof value !== 'object') return undefined;

  for (const key of ['fileName', 'filename', 'name']) {
    const candidate = (value as Record<string, unknown>)[key];
    if (typeof candidate === 'string') return candidate;
  }
  return undefined;
}

export function fileNameFromContentDisposition(disposition: string | null): string | undefined {
  if (!disposition) return undefined;
  const match = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/.exec(disposition);
  return match?.[1]?.replace(/['"]/g, '');
}
